import { UserAction } from '@enview/interface/types/actions/UserAction';
import {
  BillDetailed,
  BillDocumentSelection,
  BillVersion,
} from '@enview/interface/types/bills/Bill';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackBillVersionViewed } from '../../../analytics/BillAnalytics';
import { UserActionAPI } from '../../../api';
import { State } from '../../../dux/@types';
import { setAnnotationLineRefs } from '../../../dux/BillViewDux';
import { getTeamMode } from '../../../dux/TeamModeDux';
import featureFlags, { FrontendFeature } from '../../../featureFlags';
import { desktopMediaQuery } from '../../../helpers/mediaQueries';
import { OptionType } from '../../Shared/DropDowns/SingleSelectDropDown';
import BillTextMissing from '../BillTextMissing';
import BillViewHeader from '../BillViewHeader';
import BillText from './BillText';
import BillTextMarkdown from './BillTextMarkdown';

// Constants
const BILL_LOADING_TEXT = 'This bill is loading...';
const BILL_MISSING_TEXT =
  'No bill text has been provided by the source. Try viewing the state or federal legislative website for additional information.';

type BillTextViewProps = {
  bill: BillDetailed;
  selectedBillVersion?: BillDocumentSelection;
  displayedVersion?: BillVersion;
  setSelectedBillVersion: (version: BillDocumentSelection) => void;
};
const BillTextView = (props: BillTextViewProps): ReactElement => {
  const [showBillTextDebug, setShowBillTextDebug] = useState(false);

  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const teamModeId = useSelector(getTeamMode);

  const dispatch = useDispatch();

  const { bill, selectedBillVersion, displayedVersion, setSelectedBillVersion } = props;
  const params = useMemo(
    () => ({
      teamId: teamModeId,
      includeBillDetails: false,
      billId: bill.id,
    }),
    [teamModeId, bill.id],
  );
  const { data, isSuccess } = UserActionAPI.endpoints.getUserActions.useQuery(params);
  const displayBillText = bill && bill.versions && bill.versions.length > 0;

  const shouldUseBillTextJsx = useCallback(
    (versionId?: number): boolean => {
      // we have loaded a bill with versions, AND we are set to display the diff
      // if only old bill text (markdown) is available, change default to showDiff === false
      // to avoid performance problems
      // (this code can be removed when all bills should be re-imported to have diffText)
      if (versionId) {
        return !!bill.versions.find((v) => v.id === versionId)?.diffText;
      }
      return !!displayedVersion && !!displayedVersion.diffText;
    },
    [bill?.versions, displayedVersion],
  );
  const [showDiff, setShowDiff] = useState(shouldUseBillTextJsx);
  useEffect(() => {
    setShowDiff(shouldUseBillTextJsx());
  }, [shouldUseBillTextJsx]);

  const annotationActionsForFormat = data
    ? data.filter((action: UserAction) => {
        const { annotation } = action;
        return annotation && annotation.isNewFormat === shouldUseBillTextJsx();
      })
    : [];

  useEffect(() => {
    if (!isSuccess || !selectedBillVersion) {
      return;
    }
    dispatch(
      setAnnotationLineRefs(
        shouldUseBillTextJsx()
          ? annotationActionsForFormat.map((action: UserAction) => action.annotation!)
          : [],
      ),
    );
  }, [
    dispatch,
    shouldUseBillTextJsx,
    isSuccess,
    selectedBillVersion,
    annotationActionsForFormat,
  ]);

  const renderBillText = (): JSX.Element => {
    const debugModeAvailable = featureFlags.isFeatureEnabledForUser(
      FrontendFeature.BillTextDebug,
      orgUser,
    );
    if (displayedVersion?.diffText) {
      return (
        <div className="bill-card card">
          <div className="card-body p-0">
            <BillText
              bill={bill}
              billVersion={selectedBillVersion}
              debug={showBillTextDebug && debugModeAvailable}
              diff={displayedVersion.diffText}
              showChanges={showDiff}
            />
            {debugModeAvailable && (
              <p>
                <button onClick={() => setShowBillTextDebug(true)} type="button">
                  DEV: View source CEML
                </button>
              </p>
            )}
          </div>
        </div>
      );
    }
    // Fallback if we cannot render BillTextJSX for some reason
    return (
      <BillTextMarkdown
        annotations={annotationActionsForFormat}
        baseText={displayedVersion?.baseText ?? ''}
        billId={bill.id}
        billState={bill.session.state.abbreviation}
        billVersionId={displayedVersion?.id ?? 0}
        compareToBase={showDiff}
        debug={featureFlags.isFeatureEnabledForUser(
          FrontendFeature.BillTextDebug,
          orgUser,
        )}
        text={displayedVersion?.text ?? ''}
      />
    );
  };
  const cannotViewKnownBadCeml = !featureFlags.isFeatureEnabledForUser(
    FrontendFeature.isCanViewCemlInMissingJurisdictions,
    orgUser,
  );

  const billNotDisplayedMessage = data === null ? BILL_LOADING_TEXT : BILL_MISSING_TEXT;
  let billDisplay: ReactElement;
  /* Experimental embedding for "additional documents" - in contrast with the BillTextMissing control,
   * which proxies through the Plural backend and includes a number of jurisdiction-specific
   * hacks and workarounds, we are embedding the document directly from the source URL here.
   * This works for Oregon, but if we bring "additional documents" to a wider set of jurisdictions,
   * we'll probably need to refactor BillTextMissing to incorporate those tricks for Documents
   */
  if (bill && selectedBillVersion?.documentType === 'additionalDocument') {
    billDisplay = (
      <embed
        height="600px"
        src={
          bill.documents.find((doc) => doc.id === selectedBillVersion.documentId)
            ?.sourceUrl
        }
        type="application/pdf"
        width="100%"
      />
    );
  } else if (
    bill &&
    displayBillText &&
    displayedVersion &&
    displayedVersion.sourceDocUrl
  ) {
    if (
      displayedVersion.text === null ||
      (displayedVersion.pdfEmbedOnly && cannotViewKnownBadCeml)
    ) {
      billDisplay = <BillTextMissing bill={bill} version={displayedVersion} />;
    } else {
      billDisplay = renderBillText();
    }
  } else {
    billDisplay = <div className="bill-text">{billNotDisplayedMessage}</div>;
  }

  return (
    <div className={`main-pane bill ${desktopMediaQuery.matches ? 'col-lg-6' : ''}`}>
      {bill && (
        <BillViewHeader
          bill={bill}
          changeBillVersion={(option: OptionType<BillDocumentSelection>) => {
            setShowDiff(shouldUseBillTextJsx(option.value.documentId));
            setSelectedBillVersion(option.value);
            trackBillVersionViewed(bill, option.label, teamModeId, orgUser);
          }}
          changeDiff={(option) => setShowDiff(option.value)}
          displayedBillVersion={displayedVersion}
          selectedBillVersion={selectedBillVersion}
          selectedDiffValue={showDiff}
        />
      )}
      {billDisplay}
    </div>
  );
};

export default BillTextView;
